
import {Vue, Component, Prop} from 'vue-property-decorator';
import VacancyCard from "@/components/vacancy-search/VacancyCard.vue";
import Vacancy from "@/model/vacancy";

/**
 * Provides a Gallery view of a collection of Vacancies, including pagination.
 *
 * Each vacancy is rendered using the VacancyCard component.
 */
@Component({
  components: {VacancyCard}
})
export default class VacancyGallery extends Vue {
  @Prop({ required: true }) readonly vacancies!: Vacancy[];

  windowWidth: number = window.innerWidth;

  currentPage: number = 1;

  get isMobile(): boolean {
    return this.windowWidth < 992;
  }

  get effectivePageSize(): number {
    return this.isMobile ? 5 : 10;
  }

  get totalPages(): number {
    return Math.ceil(this.vacancies.length / this.effectivePageSize) || 1;
  }

  /**
   * Retrieve the vacancies visible for the current page.
   */
  get visibleVacancies(): Vacancy[] {
    const start = (this.currentPage - 1) * this.effectivePageSize;
    return this.vacancies.slice(start, start + this.effectivePageSize);
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  onResize(): void {
    this.$set(this, "windowWidth", window.innerWidth);

    // Ensure we don’t get stuck on an invalid page number
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  mounted() {
    window.addEventListener("resize", this.onResize, { passive: true });
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  }

  /**
   * Calculate what the pagination will look like, by generating a list of numbers (optionally separated by a `...`)
   */
  get paginationItems(): (number | string)[] {
    const pages: (number | string)[] = [];
    const { currentPage, totalPages } = this;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);
    if (currentPage > 3) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);

    return pages;
  }
}
